import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { UserPatchInput, UserPatchResponse, UsersResponse } from './contractsMirror.js';

/**
 * TanStack Query hook for the COARSE People & Access endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). The SERVER computes the directory + the role/active
 * counts (22-06); the SPA only renders the returned `UsersResponse` (no client
 * re-aggregation, no new N+1 — threat T-22-tenant / CARVE-07). The text + role filters
 * are applied in-memory in the view over the already-loaded directory, so neither is part
 * of the query key (the whole directory is one cache entry).
 *
 * The request goes through `apiFetch` (the only fetch path — `credentials:'include'`
 * + `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived
 * server-side from the auth context; the SPA NEVER sends a tenant id param (threat
 * T-22-tenant).
 */

/** The stable query key for the users directory (one cache entry for the workspace). */
export function usersQueryKey(): readonly unknown[] {
  return ['users'] as const;
}

export function useUsersQuery() {
  return useQuery<UsersResponse>({
    queryKey: usersQueryKey(),
    queryFn: () => apiFetchJson<UsersResponse>('/api/v1/users'),
  });
}

/**
 * The edit-user-record write hook (PATCH /api/v1/users/:id, Plan 22-24): deactivate/reactivate,
 * admin password reset, name/email/employee-link edit. Clones the usePermissionsMutation shape —
 * rides `apiFetch` (CSRF double-submit) with NO tenantId (SEC-11). On a guard rejection the thrown
 * `ApiError` carries the server code (e.g. `last_active_admin` on 409) for the view to surface. On
 * success it invalidates the directory so the list re-syncs to the authoritative server state.
 */
export function useUserPatchMutation() {
  const queryClient = useQueryClient();
  return useMutation<UserPatchResponse, Error, { userId: number } & UserPatchInput>({
    mutationFn: ({ userId, ...input }) =>
      apiFetchJson<UserPatchResponse>(`/api/v1/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersQueryKey() });
    },
  });
}
